import { useEffect, useRef, useState } from 'react';

import type { RoleOption } from './AgentsView';

interface Props {
  roles: RoleOption[];
  onCancel: () => void;
  onSubmit: (input: {
    name: string;
    slug: string;
    description: string;
    role_id: string;
    include_soul_md: boolean;
  }) => Promise<void> | void;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function CreateAgentModal({ roles, onCancel, onSubmit }: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [roleId, setRoleId] = useState<string>(roles[0]?.id ?? '');
  const [includeSoul, setIncludeSoul] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const canSubmit = name.trim().length > 0 && slug.length > 0 && roleId !== '';

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        slug,
        description: description.trim(),
        role_id: roleId,
        include_soul_md: includeSoul,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create agent.');
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-label="New agent"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={onCancel}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.36)',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 120ms ease',
        }}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        style={{
          position: 'relative',
          width: 'min(520px, 100%)',
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 10,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 20px 40px rgba(0,0,0,0.08)',
          padding: 24,
          transform: mounted ? 'scale(1)' : 'scale(0.97)',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 180ms ease, transform 180ms ease',
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: 'var(--text-soft)',
            marginBottom: 6,
          }}
        >
          Provision
        </div>
        <div
          style={{
            fontFamily: HEADING_SERIF,
            fontSize: 24,
            fontWeight: 500,
            color: 'var(--text)',
            letterSpacing: '-0.018em',
            lineHeight: 1.15,
            marginBottom: 6,
          }}
        >
          A new agent
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--text-soft)',
            lineHeight: 1.5,
            marginBottom: 22,
          }}
        >
          Define identity, scope its role, and we&apos;ll scaffold AGENT.md in your git repo.
        </div>

        <Field label="Display name" hint="Shown to operators and on tasks.">
          <input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Correspondence Agent"
            style={inputStyle}
          />
        </Field>

        <Field label="Slug" hint="Technical handle. Lowercase, kebab-case.">
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              overflow: 'hidden',
              background: 'var(--bg)',
            }}
          >
            <span
              style={{
                padding: '7px 10px',
                background: 'var(--bg-alt)',
                borderRight: '1px solid var(--border)',
                fontFamily: MONO,
                fontSize: 12,
                color: 'var(--text-soft)',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              @
            </span>
            <input
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugTouched(true);
              }}
              placeholder="correspondence"
              style={{
                flex: 1,
                padding: '7px 10px',
                fontSize: 13,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--text)',
                fontFamily: MONO,
                letterSpacing: '0.01em',
              }}
            />
          </div>
        </Field>

        <Field label="Description" hint="A one-liner. Becomes the AGENT.md `description` field.">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Reads support inbox, drafts replies, flags escalations for human review."
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 72,
              lineHeight: 1.5,
            }}
          />
        </Field>

        <Field label="Role">
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {roles.length === 0 && <option value="">No roles available</option>}
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>

        <div
          style={{
            marginTop: 18,
            padding: '12px 14px',
            background: 'var(--bg-alt)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <ToggleSwitch
            checked={includeSoul}
            onChange={setIncludeSoul}
            id="include-soul"
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <label
              htmlFor="include-soul"
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--text)',
                cursor: 'pointer',
                display: 'block',
              }}
            >
              Include SOUL.md scaffold
            </label>
            <div
              style={{
                marginTop: 3,
                fontSize: 11.5,
                color: 'var(--text-soft)',
                lineHeight: 1.45,
              }}
            >
              Adds a stub personality file you can fill in later.
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              padding: '8px 12px',
              fontSize: 12,
              color: 'var(--kind-failure)',
              background: 'color-mix(in srgb, var(--kind-failure) 8%, transparent)',
              border: '1px solid color-mix(in srgb, var(--kind-failure) 30%, transparent)',
              borderRadius: 5,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={secondaryButtonStyle}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            style={{
              ...primaryButtonStyle,
              opacity: !canSubmit || submitting ? 0.55 : 1,
              cursor: !canSubmit || submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Provisioning…' : 'Provision agent'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--text-soft)',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: 'var(--text-soft)',
            lineHeight: 1.4,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  id: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      onClick={() => onChange(!checked)}
      style={{
        width: 32,
        height: 18,
        flexShrink: 0,
        marginTop: 1,
        padding: 0,
        background: checked ? 'var(--accent)' : 'var(--bg)',
        border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-strong)'}`,
        borderRadius: 999,
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 140ms ease, border-color 140ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 1,
          left: checked ? 15 : 1,
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
          transition: 'left 140ms ease',
        }}
      />
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--bg)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '8px 14px',
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  fontSize: 12.5,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
  letterSpacing: '0.01em',
  transition: 'background 120ms ease, opacity 120ms ease',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '8px 14px',
  background: 'transparent',
  color: 'var(--text-mute)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 12.5,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
};

const HEADING_SERIF =
  "ui-serif, Georgia, 'Iowan Old Style', 'Apple Garamond', Palatino, serif";
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
